/**
 * Pure logic for the Schema editor: what fleet-admins may change, held as a
 * draft; how a draft becomes the PATCH the API takes; and how a change reads
 * in the review step and the history.
 *
 * The API decides what is legal (see schema_overlay.py); this mirrors only
 * enough of it to catch an obvious mistake before the round trip — an empty
 * label, a malformed or duplicate value — never to be a second copy of the
 * rules. Protected values are the API's call too: the editor merely shows the
 * lock the schema carries.
 */
import {
  ConfigSchema,
  EnumControl,
  Glyph,
  SchemaChange,
  SchemaField,
  SchemaFieldPatch,
  SchemaOption,
  SchemaOptionPatch,
  SchemaPatch,
  Severity,
} from "./configTypes";
import { SEGMENT_MAX_OPTIONS } from "./FieldControl";

export interface DraftOption {
  /**
   * Identity of the ROW for the editor's lifetime -- never sent to the API.
   *
   * React keys the option rows on this. It used to key them on `value`, and a
   * new option's value follows its label as the admin types, so every
   * keystroke changed the key, React replaced the row, and the input the
   * admin was typing in was gone after one character (SW-7539). An existing
   * option's value cannot change in the editor, so it serves as its id; a new
   * option gets a counter, which stays put however its value and label move.
   */
  id: string;
  value: string;
  label: string;
  severity: Severity;
  glyph: Glyph | null;
  help: string;
  rollup_bucket: string | null;
  retired: boolean;
  /** The backend executes on this value; the editor may relabel, not retire. */
  protected: boolean;
  /** Added in this session — its value is still the admin's to type. */
  isNew: boolean;
}

export interface DraftField {
  name: string;
  label: string;
  help: string;
  order: number;
  control: EnumControl | null;
  stale_after_days: number | null;
  /** null for anything that is not an enum. */
  options: DraftOption[] | null;
}

export type SchemaDraft = Record<string, DraftField>;

export const SEVERITIES: Severity[] = ["ok", "warn", "bad", "neutral"];
export const GLYPHS: Glyph[] = ["check", "half", "cross", "dash"];
export const VALUE_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;

/** The fields an admin may shape: everything the API does not maintain itself. */
export const editableFields = (schema: ConfigSchema): SchemaField[] =>
  schema.fields.filter((f) => !f.system);

const optionDraft = (o: SchemaOption): DraftOption => ({
  id: `existing:${o.value}`,
  value: o.value,
  label: o.label,
  severity: o.severity,
  glyph: o.glyph ?? null,
  help: o.help ?? "",
  rollup_bucket: o.rollup_bucket ?? null,
  retired: !!o.retired,
  protected: !!o.protected,
  isNew: false,
});

export const fieldDraft = (f: SchemaField): DraftField => ({
  name: f.name,
  label: f.label,
  help: f.help ?? "",
  order: f.order,
  control: f.control ?? null,
  stale_after_days: f.stale_after_days ?? null,
  options: f.type === "enum" ? (f.options ?? []).map(optionDraft) : null,
});

export const draftFromSchema = (schema: ConfigSchema): SchemaDraft =>
  Object.fromEntries(
    editableFields(schema).map((f) => [f.name, fieldDraft(f)]),
  );

/** Whether a field's freshness horizon is the admin's to tune. */
export const hasHorizon = (f: SchemaField): boolean =>
  !!f.confirmable || !!f.stale_after_days;

/** The fleet-counter buckets this field's options may feed (allocation only). */
export const bucketsFor = (f: SchemaField): string[] => {
  const seen: string[] = [];
  for (const o of f.options ?? []) {
    if (o.rollup_bucket && !seen.includes(o.rollup_bucket)) {
      seen.push(o.rollup_bucket);
    }
  }
  return seen;
};

/** What the console would draw, given the draft and the rule of thumb. */
export const effectiveControl = (d: DraftField): EnumControl =>
  d.control ??
  ((d.options ?? []).filter((o) => !o.retired).length <= SEGMENT_MAX_OPTIONS
    ? "segmented"
    : "select");

export const controlLabel = (c: EnumControl): string =>
  c === "segmented" ? "Chiclets" : "Dropdown";

/**
 * A stored value from a label: "Charter ops" → CHARTER_OPS. Uniqueness is the
 * one thing that matters more than prettiness, so a clash gets a suffix.
 */
export function suggestValue(label: string, taken: string[]): string {
  let base = label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
  if (!base || !/^[A-Z]/.test(base)) base = `OPTION${base ? `_${base}` : ""}`;
  let candidate = base;
  let n = 2;
  while (taken.includes(candidate)) candidate = `${base}_${n++}`;
  return candidate;
}

// One editor session's counter; a new row's id only has to be unique among
// the rows on screen, and must not change when its value or label does.
let newOptionSerial = 0;

export const newOption = (label: string, taken: string[]): DraftOption => ({
  id: `new:${++newOptionSerial}`,
  value: suggestValue(label, taken),
  label,
  severity: "neutral",
  glyph: null,
  help: "",
  rollup_bucket: null,
  retired: false,
  protected: false,
  isNew: true,
});

/** The draft as the schema would render it — for the live preview. */
export const previewField = (
  base: SchemaField,
  d: DraftField,
): SchemaField => ({
  ...base,
  label: d.label,
  help: d.help || undefined,
  control: d.control ?? undefined,
  stale_after_days: d.stale_after_days ?? undefined,
  options: d.options
    ? d.options.map((o) => ({
        value: o.value,
        label: o.label,
        severity: o.severity,
        ...(o.glyph ? { glyph: o.glyph } : {}),
        ...(o.help ? { help: o.help } : {}),
        ...(o.rollup_bucket ? { rollup_bucket: o.rollup_bucket } : {}),
        ...(o.retired ? { retired: true } : {}),
        ...(o.protected ? { protected: true } : {}),
      }))
    : base.options,
});

/**
 * Obvious mistakes, caught before the round trip. Returns null when the field
 * is fine to send; the API remains the authority on everything else.
 */
export function problemWith(d: DraftField): string | null {
  if (!d.label.trim()) return "The field needs a label.";
  if (
    d.stale_after_days !== null &&
    (!Number.isInteger(d.stale_after_days) || d.stale_after_days < 1)
  ) {
    return "The freshness horizon must be a whole number of days, at least 1.";
  }
  if (!d.options) return null;
  const seen = new Set<string>();
  for (const o of d.options) {
    if (!o.label.trim()) return `Option ${o.value || "(new)"} needs a label.`;
    if (!VALUE_RE.test(o.value)) {
      return `"${o.value}" is not a usable value — letters, digits, _ or -, starting with a letter.`;
    }
    if (seen.has(o.value)) return `Two options share the value ${o.value}.`;
    seen.add(o.value);
    if (o.retired && o.protected)
      return `${o.label} is protected and cannot be retired.`;
  }
  return null;
}

const same = <T>(a: T, b: T): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** A field as the API wants it — whole, every time it is sent. */
export const fieldPatch = (d: DraftField): SchemaFieldPatch => ({
  label: d.label.trim(),
  help: d.help.trim() || null,
  order: d.order,
  ...(d.control ? { control: d.control } : {}),
  ...(d.stale_after_days !== null
    ? { stale_after_days: d.stale_after_days }
    : {}),
  ...(d.options
    ? {
        options: d.options.map(
          (o): SchemaOptionPatch => ({
            value: o.value,
            label: o.label.trim(),
            severity: o.severity,
            glyph: o.glyph,
            help: o.help.trim() || null,
            rollup_bucket: o.rollup_bucket,
            retired: o.retired,
          }),
        ),
      }
    : {}),
});

/** Only the fields that moved, each whole — the per-field-replace contract. */
export function buildPatch(
  original: SchemaDraft,
  edited: SchemaDraft,
  version: number,
): SchemaPatch {
  const fields: SchemaPatch["fields"] = {};
  for (const [name, d] of Object.entries(edited)) {
    if (!same(original[name], d)) fields[name] = fieldPatch(d);
  }
  return { schema_version: version, fields };
}

export interface DraftChange {
  field: string;
  fieldLabel: string;
  text: string;
}

/** The review list: one line per thing that changed, in the admin's words. */
export function describeDraft(
  original: SchemaDraft,
  edited: SchemaDraft,
): DraftChange[] {
  const out: DraftChange[] = [];
  for (const [name, d] of Object.entries(edited)) {
    const o = original[name];
    if (!o) continue;
    const add = (text: string) =>
      out.push({ field: name, fieldLabel: d.label, text });
    if (o.label !== d.label) add(`Label: “${o.label}” → “${d.label}”`);
    if (o.help !== d.help)
      add(d.help ? `Help text changed` : `Help text removed`);
    if (o.control !== d.control) {
      add(
        `Control: ${controlLabel(effectiveControl(o))} → ${controlLabel(effectiveControl(d))}${d.control ? "" : " (rule of thumb)"}`,
      );
    }
    if (o.stale_after_days !== d.stale_after_days) {
      add(
        `Freshness horizon: ${o.stale_after_days ?? "—"} → ${d.stale_after_days ?? "—"} days`,
      );
    }
    if (o.options && d.options) {
      const before = new Map(o.options.map((x) => [x.value, x]));
      for (const x of d.options) {
        const prev = before.get(x.value);
        if (!prev) {
          add(
            `Option added: ${x.label} (${x.value})${x.rollup_bucket ? ` · counts toward ${x.rollup_bucket}` : ""}`,
          );
          continue;
        }
        if (prev.retired !== x.retired)
          add(
            x.retired
              ? `Option retired: ${x.label}`
              : `Option restored: ${x.label}`,
          );
        if (prev.label !== x.label)
          add(`Option relabelled: “${prev.label}” → “${x.label}”`);
        if (prev.severity !== x.severity)
          add(`${x.label}: colour ${prev.severity} → ${x.severity}`);
        if (prev.glyph !== x.glyph)
          add(
            `${x.label}: glyph ${prev.glyph ?? "none"} → ${x.glyph ?? "none"}`,
          );
        if (prev.help !== x.help) add(`${x.label}: help text changed`);
        if (prev.rollup_bucket !== x.rollup_bucket) {
          add(`${x.label}: counts toward ${x.rollup_bucket ?? "nothing"}`);
        }
      }
      const order = (xs: DraftOption[]) =>
        xs.map((x) => x.value).filter((v) => before.has(v));
      if (!same(order(o.options), order(d.options))) add("Options reordered");
    }
  }
  return out;
}

/** A history entry's change, in the same words the review uses. */
export function describeHistoryChange(
  c: SchemaChange,
  schema: ConfigSchema,
): string {
  const field = schema.fields.find((f) => f.name === c.field)?.label ?? c.field;
  const who = c.label ?? c.value ?? "";
  switch (c.kind) {
    case "option_added":
      return `${field}: option added — ${who}`;
    case "option_retired":
      return `${field}: option retired — ${who}`;
    case "option_restored":
      return `${field}: option restored — ${who}`;
    case "option_order":
      return `${field}: options reordered`;
    case "label":
      return `${field}: label “${String(c.from)}” → “${String(c.to)}”`;
    case "control":
      return `${field}: control → ${c.to ? controlLabel(c.to as EnumControl) : "rule of thumb"}`;
    case "stale_after_days":
      return `${field}: freshness horizon ${String(c.from ?? "—")} → ${String(c.to ?? "—")} days`;
    case "help":
      return `${field}: help text changed`;
    case "order":
      return `${field}: moved`;
    default:
      if (c.kind.startsWith("option_")) {
        return `${field}: ${c.value} ${c.kind.slice("option_".length)} ${String(c.from ?? "—")} → ${String(c.to ?? "—")}`;
      }
      return `${field}: ${c.kind}`;
  }
}
